import React from 'react';
import PropTypes from 'prop-types';
import { X, ArrowRight } from 'lucide-react';
import { METHOD_DESCRIPTIONS } from '../../utils/valuationConstants';

/**
 * MethodDescriptionsModal - Display all valuation methods with detailed pros/cons
 * Allows users to select a method directly from the modal
 */
const MethodDescriptionsModal = ({ isOpen, onClose, onSelectMethod }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Valuation Methods Explained</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <X size={24} />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Click any method below to select it for your valuation.
          </p>

          <div className="space-y-6">
            {Object.entries(METHOD_DESCRIPTIONS).map(([key, method]) => (
              <button
                key={key}
                onClick={() => onSelectMethod(key)}
                className="w-full text-left border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">{method.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{method.name}</h3>
                    <p className="text-sm text-gray-700 mt-1">{method.description}</p>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                    Select
                    <ArrowRight size={16} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-xs font-semibold text-green-700 mb-1">✓ PROS</p>
                    <p className="text-sm text-gray-600">{method.pros}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-orange-700 mb-1">✗ CONS</p>
                    <p className="text-sm text-gray-600">{method.cons}</p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-blue-700 mb-1">BEST FOR</p>
                  <p className="text-sm text-gray-700">{method.bestFor}</p>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

MethodDescriptionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectMethod: PropTypes.func.isRequired
};

export default MethodDescriptionsModal;
